import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Clock, Play, Square, CheckCircle2, Loader2, AlertCircle, DollarSign, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const f$ = (n) => `$${Number(n || 0).toFixed(2)}`;
const fH = (n) => `${Number(n || 0).toFixed(2)}h`;

const fmtDT = (iso) => iso ? new Date(iso).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';

export default function DriverClockMobile() {
  const { driverId } = useParams();
  const [driver, setDriver] = useState(null);
  const [openShift, setOpenShift] = useState(null);
  const [todayShifts, setTodayShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [showTipPrompt, setShowTipPrompt] = useState(false);
  const [tipInput, setTipInput] = useState('0');

  const load = useCallback(async () => {
    try {
      // Driver lookup
      const drvs = await fetch(`${API}/api/command-center/drivers`).then(r => r.json());
      const me = (drvs.drivers || []).find(d => d.driver_id === driverId);
      if (!me) { setError('Driver not found. Check the link with your manager.'); setLoading(false); return; }
      setDriver(me);
      // Today's shifts
      const today = new Date().toISOString().slice(0, 10);
      const monday = (() => { const d = new Date(today); d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() || 7) - 1)); return d.toISOString().slice(0,10); })();
      const r = await fetch(`${API}/api/drivers/${driverId}/shifts?week_start=${monday}`).then(r => r.json());
      const shifts = r.shifts || [];
      setOpenShift(shifts.find(s => s.status === 'open') || null);
      // Just today
      setTodayShifts(shifts.filter(s => (s.start_at || '').slice(0, 10) === today));
      setLoading(false);
    } catch (e) {
      setError(e.message); setLoading(false);
    }
  }, [driverId]);

  useEffect(() => { load(); }, [load]);

  // Live elapsed timer for the open shift
  useEffect(() => {
    if (!openShift) { setElapsed(0); return; }
    const tick = () => {
      const start = new Date(openShift.start_at).getTime();
      setElapsed(Math.floor((Date.now() - start) / 1000));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [openShift]);

  const clockIn = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/drivers/${driverId}/clock-in`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source: 'mobile' }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Clock-in failed');
      toast.success('Clocked in!', { duration: 1200, position: 'top-center' });
      await load();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const askClockOut = () => setShowTipPrompt(true);

  const finishClockOut = async () => {
    setBusy(true); setShowTipPrompt(false);
    try {
      const r = await fetch(`${API}/api/drivers/shifts/${openShift.shift_id}/clock-out`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ gratuity: parseFloat(tipInput) || 0 }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Clock-out failed');
      toast.success(`Shift complete · ${fH(data.hours)} logged`, { position: 'top-center' });
      setTipInput('0');
      await load();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  if (loading) return <Center>Loading…</Center>;
  if (error) return (
    <Center>
      <AlertCircle className="h-10 w-10 text-red-500 mb-3" />
      <p className="text-base text-white">{error}</p>
      <Link to="/" className="mt-4 text-xs text-gray-400 hover:text-white underline">← Back to ACE</Link>
    </Center>
  );

  const hh = Math.floor(elapsed / 3600);
  const mm = Math.floor((elapsed % 3600) / 60);
  const ss = elapsed % 60;
  const totalToday = todayShifts.reduce((s, x) => s + (x.hours || 0), 0);
  const totalTodayPay = todayShifts.reduce((s, x) => s + ((x.hours || 0) * (x.hourly_rate || 0)) + (x.gratuity || 0), 0);

  return (
    <div className="min-h-screen bg-black text-white flex flex-col" data-testid="driver-clock">
      {/* Hero */}
      <div className="px-5 pt-8 pb-6 border-b border-gray-900">
        <Link to="/" className="inline-flex items-center text-[10px] uppercase tracking-widest text-gray-500 hover:text-white mb-3">
          <ChevronLeft className="h-3 w-3 mr-1" /> Back
        </Link>
        <p className="text-[10px] uppercase tracking-[0.3em] text-red-500 font-bold">ACE · Driver Clock</p>
        <h1 className="text-3xl font-black tracking-tight mt-2">{driver.name}</h1>
        <p className="text-xs text-gray-400 mt-1">{f$(driver.hourly_rate || 0)}/hr · {driver.pay_method || 'ACH'}</p>
      </div>

      {/* Main action area */}
      <div className="flex-1 flex flex-col items-center justify-center px-5 py-10">
        {openShift ? (
          <>
            <p className="text-[10px] uppercase tracking-[0.3em] text-emerald-500 font-bold mb-2">Currently On Shift</p>
            <div className="text-6xl font-black font-mono tabular-nums tracking-tight" data-testid="elapsed">
              {String(hh).padStart(2, '0')}:{String(mm).padStart(2, '0')}:{String(ss).padStart(2, '0')}
            </div>
            <p className="text-xs text-gray-400 mt-2">Started {fmtDT(openShift.start_at)}</p>
            <button
              onClick={askClockOut}
              disabled={busy}
              className="mt-10 h-32 w-32 rounded-full bg-red-600 hover:bg-red-700 active:scale-95 transition-all flex flex-col items-center justify-center shadow-[0_0_40px_rgba(220,38,38,0.5)] disabled:opacity-50"
              data-testid="clock-out-btn"
            >
              {busy ? <Loader2 className="h-10 w-10 animate-spin" /> : <Square className="h-10 w-10 fill-white" />}
              <span className="text-[10px] uppercase tracking-widest font-bold mt-1">Clock Out</span>
            </button>
            <p className="text-[10px] text-gray-500 mt-6 max-w-xs text-center">Tap to end shift. You'll be asked for any tips received before saving.</p>
          </>
        ) : (
          <>
            <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 font-bold mb-6">Off Shift</p>
            <button
              onClick={clockIn}
              disabled={busy}
              className="h-40 w-40 rounded-full bg-emerald-600 hover:bg-emerald-500 active:scale-95 transition-all flex flex-col items-center justify-center shadow-[0_0_60px_rgba(16,185,129,0.5)] disabled:opacity-50"
              data-testid="clock-in-btn"
            >
              {busy ? <Loader2 className="h-12 w-12 animate-spin" /> : <Play className="h-12 w-12 fill-white" />}
              <span className="text-xs uppercase tracking-widest font-bold mt-2">Clock In</span>
            </button>
            <p className="text-[10px] text-gray-500 mt-8 max-w-xs text-center">Tap when you're starting work. Your start time is set to now.</p>
          </>
        )}
      </div>

      {/* Today's shifts */}
      <div className="bg-gray-950 border-t border-gray-900 px-5 py-5">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 font-bold">Today</p>
          <p className="text-[10px] uppercase tracking-widest text-gray-500">{fH(totalToday)} · {f$(totalTodayPay)}</p>
        </div>
        {todayShifts.length === 0 ? (
          <p className="text-xs text-gray-500 italic text-center py-4">No shifts yet today.</p>
        ) : (
          <div className="space-y-2">
            {todayShifts.map(s => {
              const pay = (s.hours || 0) * (s.hourly_rate || 0) + (s.gratuity || 0);
              return (
                <div key={s.shift_id} className="bg-black border border-gray-800 rounded-lg px-3 py-2 flex items-center justify-between" data-testid={`shift-${s.shift_id}`}>
                  <div className="text-xs">
                    <p className="font-bold">{s.start_at ? new Date(s.start_at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) : '—'} → {s.end_at ? new Date(s.end_at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) : <span className="text-emerald-400">now</span>}</p>
                    <p className="text-[10px] text-gray-500 mt-0.5">{fH(s.hours)} · {s.status.toUpperCase()}{s.gratuity ? ` · +${f$(s.gratuity)} tip` : ''}</p>
                  </div>
                  <p className="font-bold text-green-500">{f$(pay)}</p>
                </div>
              );
            })}
          </div>
        )}
        <div className="mt-3 pt-3 border-t border-gray-800 flex items-center justify-between">
          <p className="text-[10px] text-gray-500">Last 7 days · all shifts visible in your paystub</p>
          <Clock className="h-3 w-3 text-gray-600" />
        </div>
      </div>

      {/* Tip prompt modal */}
      {showTipPrompt && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setShowTipPrompt(false)}>
          <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()} data-testid="tip-prompt">
            <div className="flex items-center gap-2 mb-3">
              <DollarSign className="h-5 w-5 text-emerald-500" />
              <h3 className="font-bold text-lg">Any tips for this shift?</h3>
            </div>
            <p className="text-xs text-gray-400 mb-4">Enter the total in tips received. Leave at $0 if none.</p>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              value={tipInput}
              onChange={e => setTipInput(e.target.value)}
              autoFocus
              className="w-full h-14 text-3xl font-bold text-center bg-black border-2 border-gray-800 rounded-lg focus:border-emerald-500 outline-none"
              data-testid="tip-input"
            />
            <div className="grid grid-cols-2 gap-2 mt-4">
              <button onClick={() => setShowTipPrompt(false)} className="h-12 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm font-semibold">Cancel</button>
              <button onClick={finishClockOut} disabled={busy} className="h-12 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-sm font-bold disabled:opacity-50" data-testid="confirm-clock-out">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : 'Confirm & Clock Out'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Center = ({ children }) => (
  <div className="min-h-screen bg-black flex flex-col items-center justify-center text-center px-5 text-sm text-gray-400">{children}</div>
);
